import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Plus, Trash, MagicWand } from "@phosphor-icons/react";
import { Section, Pill, EmptyState } from "./_shared";
import { AIEnrichButton, AIBulkGenerateModal, AIAssistant } from "./_ai";

export default function AssumptionLens({ projectId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statement, setStatement] = useState("");
  const [category, setCategory] = useState("");
  const [creating, setCreating] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/assumptions`);
      setItems(data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [projectId]);

  const add = async (e) => {
    e.preventDefault();
    if (!statement.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/assumptions`, { statement, category: category || "general" });
      setItems((p) => [data, ...p]);
      setStatement("");
      setCategory("");
    } catch { toast.error("Failed to add"); }
    finally { setCreating(false); }
  };

  const handleEnriched = (enriched) => {
    if (enriched?.statement) setStatement(enriched.statement);
    if (enriched?.category) setCategory(enriched.category);
  };

  const analyze = async (id) => {
    setAnalyzingId(id);
    try {
      const { data } = await api.post(`/projects/${projectId}/assumptions/${id}/analyze`);
      setItems((p) => p.map(x => x.id === id ? data : x));
      toast.success("Analysis complete");
    } catch { toast.error("Analysis failed"); }
    finally { setAnalyzingId(null); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/projects/${projectId}/assumptions/${id}`);
      setItems((p) => p.filter(x => x.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="assumption-lens">
      <Section
        eyebrow="Validate"
        title="Add an assumption"
        action={
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            className="inline-flex items-center gap-2 h-10 px-4 bg-zinc-950 text-white text-xs font-bold hover:bg-[#0020F5]"
            data-testid="bulk-gen-assumption-button"
          >
            <MagicWand size={14} weight="fill" /> AI bulk generate
          </button>
        }
      >
        <form onSubmit={add} className="flex flex-col sm:flex-row gap-3">
          <input
            value={statement} onChange={(e) => setStatement(e.target.value)}
            placeholder='e.g. "Vendor X will deliver API specs by Q2."'
            className="flex-1 h-12 px-4 border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
            data-testid="assumption-input"
          />
          <input
            value={category} onChange={(e) => setCategory(e.target.value)}
            placeholder="Category (optional)"
            className="sm:w-40 h-12 px-4 border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
            data-testid="assumption-category-input"
          />
          <AIEnrichButton
            projectId={projectId}
            lens="assumption"
            partial={{ statement }}
            onEnriched={handleEnriched}
          />
          <button type="submit" disabled={creating}
            className="h-12 px-6 bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="add-assumption-button">
            <Plus size={16} weight="bold" /> {creating ? "Adding..." : "Add"}
          </button>
        </form>
      </Section>

      <Section eyebrow="NLP scoring" title={`Assumptions (${items.length})`}>
        {loading ? (
          <div className="text-zinc-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState title="No assumptions yet" hint="Add one above to run AI validity scoring." testid="empty-assumptions" />
        ) : (
          <div className="divide-y divide-zinc-200 border border-zinc-200">
            {items.map((a) => (
              <div key={a.id} className="p-5" data-testid={`assumption-item-${a.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-base text-zinc-900">{a.statement}</p>
                    {a.validity_score != null ? (
                      <div className="mt-3 grid sm:grid-cols-3 gap-3">
                        <div>
                          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Validity</div>
                          <div className="flex items-end gap-2 mt-1">
                            <div className="font-display font-black text-3xl" style={{ color: a.validity_score >= 70 ? "#0020F5" : a.validity_score >= 40 ? "#FF9500" : "#FF3B30" }}>{a.validity_score}</div>
                            <div className="text-zinc-400 text-sm pb-1">/100</div>
                          </div>
                          <div className="w-full h-2 bg-zinc-100 mt-2">
                            <div className="h-2" style={{ width: `${a.validity_score}%`, background: a.validity_score >= 70 ? "#0020F5" : a.validity_score >= 40 ? "#FF9500" : "#FF3B30" }} />
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Confidence</div>
                          <div className="mt-2"><Pill tone={a.confidence === "high" ? "blue" : a.confidence === "low" ? "red" : "orange"}>{a.confidence}</Pill></div>
                        </div>
                        <div>
                          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Risks if wrong</div>
                          <ul className="mt-2 text-xs text-zinc-700 space-y-1 list-disc list-inside">
                            {(a.risks_if_wrong || []).slice(0, 3).map((r, i) => <li key={`${a.id}-rww-${i}`}>{r}</li>)}
                          </ul>
                        </div>
                        {a.reasoning && (
                          <div className="sm:col-span-3">
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Reasoning</div>
                            <p className="text-sm text-zinc-700 mt-1">{a.reasoning}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-zinc-500">Not yet analysed.</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <button
                      onClick={() => analyze(a.id)} disabled={analyzingId === a.id}
                      className="inline-flex items-center gap-2 h-10 px-4 bg-[#0020F5] text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-50"
                      data-testid={`analyze-assumption-${a.id}`}
                    >
                      <Sparkle size={14} weight="fill" />
                      {analyzingId === a.id ? "Analysing…" : (a.validity_score != null ? "Re-analyse" : "Analyse")}
                    </button>
                    <button onClick={() => remove(a.id)} className="inline-flex items-center justify-center h-10 px-4 border border-zinc-300 text-zinc-700 hover:text-[#FF3B30] hover:border-[#FF3B30]" data-testid={`delete-assumption-${a.id}`}>
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
      <AIBulkGenerateModal
        projectId={projectId}
        lens="assumption"
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onImported={() => load()}
      />
      <AIAssistant projectId={projectId} lens="assumption" label="Assumption" />
    </div>
  );
}
